import React, {useEffect, useRef} from 'react';
import {ScrollView, View, Text, Image, Pressable, Animated} from 'react-native';
import {usePowerSyncData} from '../../Providers/PowerSyncDataProvider';
import {ObjectId, UUID} from 'bson';
import {
  Submission,
  Submission_answer,
  SubmissionStatus,
  SubmissionLink,
  TemplateVersion,
} from '../../Models/PowerSyncModels/models';
import {SubmissionStatuses} from '../../Constants/SubmissionStatuses';
import {CommonStyleSheet} from '../../Styles/Shared/CommonStyles';
import {ControlsStyleSheet} from '../../Styles/Shared/Controls';
import {DynamicPageStyleSheet} from '../../Styles/DynamicPageStyles';
import {Navigation, Widgets} from '../../Constants/Navigation';
import {Images} from '../../Constants/Images';
import Colors from '../../Styles/Shared/Colors';
import Icon from '../Shared/Icon';
import {TextInput} from 'react-native';
import {LOCK_STATUS, METADATA_KEYS} from '../../Constants/AppConstants';
import {usePowerSyncUser} from '../../Providers/PowerSyncUserProvider';
import usePowerSyncWriteData from '../../Hooks/usePowerSyncWriteData';

type FormConfirmationProps = {
  navigation: any;
  submissionId: string;
  title?: string;
  project?: string;
};

const FormConfirmation = (props: FormConfirmationProps): React.ReactElement => {
  const circleSize = useRef(new Animated.Value(80)).current;
  const backgroundOpacity = useRef(new Animated.Value(0)).current;
  const textBottom = useRef(new Animated.Value(-100)).current;

  const {
    templates,
    templateVersions,
    submissions,
    submissionStatuses: dbsubmissionStatuses,
    SQLUsers,
  } = usePowerSyncData();

  const user = usePowerSyncUser();

  const {createSubmission, createSubmissionLink} = usePowerSyncWriteData();

  useEffect(() => {
    Animated.timing(circleSize, {
      toValue: 600,
      duration: 1000,
      delay: 400,
      useNativeDriver: false,
    }).start(() => {
      Animated.timing(backgroundOpacity, {
        toValue: 1,
        duration: 1000,
        useNativeDriver: true,
      }).start(() => {
        Animated.timing(textBottom, {
          toValue: 24,
          duration: 1000,
          useNativeDriver: false,
        }).start();
      });
    });
  }, []);

  const goToDashboard = () => {
    props.navigation.pop();

    props.navigation.navigate(Navigation.HOME, {widget: Widgets.HOME});
  };

  const closeForm = () => {
    props.navigation.pop();
  };

  async function followUp(): Promise<void> {
    props.navigation.pop();

    let currentUser = user.getUserInfo();
    let org = '';
    let currentEmail = '';
    if (currentUser) {
      org = currentUser.orgId.toString();
      currentEmail = currentUser.email;
    }

    let template = templates.find(t => t.name === 'Create a Task');
    if (template) {
      let templateId = template._id;
      let versions = templateVersions
        .filter(x => x.templateId === templateId)
        .sort((a, b) => b.version - a.version);
      if (versions[0]) {
        let newDraftId: string = new ObjectId().toString();
        const draftStatus: SubmissionStatus = dbsubmissionStatuses.find(
          x => x.name == SubmissionStatuses.DRAFT,
        )!;

        let title = 'Follow up on ' + props.title;
        if (props.project) title += ' at ' + props.project;

        let answers = await getFollowUpAnswers(versions[0]);
        let newSubmission: Submission = {
          _id: newDraftId,
          partition: org,
          createdBy: currentEmail as string,
          updatedBy: currentEmail as string,
          templateVersion: {
            _id: versions[0]._id,
            name: versions[0].name,
          },
          templateType: {
            _id: template!.templateType._id,
            name: template!.templateType.name,
          },
          submissionStatus: {
            _id: draftStatus._id,
            name: draftStatus.name,
          },
          SQLServerId: new UUID().toString().toUpperCase(),
          answers: answers,
          metadataJSON: '{"' + METADATA_KEYS.TITLE + '":"' + title + '"}',
          dataHubVersion: Math.floor(Date.now() / 1000),
          attachmentsUploaded: true,
          submissionStatusLock: LOCK_STATUS.UNLOCK,
          createDateTimeStamp: new Date(),
          updateDateTimeStamp: new Date(),
        };

        await createSubmission(newSubmission);

        if (newDraftId == null) throw Error('Error: create draft failed!');

        let newSubmissionLink: SubmissionLink = {
          _id: new ObjectId().toString(),
          parentSubmissionId: props.submissionId,
          childSubmissionId: newDraftId,
          partition: org,
        };

        await createSubmissionLink(newSubmissionLink);

        props.navigation.navigate(Navigation.DYNAMICFORM, {
          submissionId: newDraftId,
          showConfirmation: false,
          submissionStatus: SubmissionStatuses.DRAFT,
        });
      }
    }
  }

  async function getFollowUpAnswers(
    version: TemplateVersion,
  ): Promise<Submission_answer[]> {
    let answers: Submission_answer[] = [];

    let users = SQLUsers;
    const currentUser = user.getUserInfo();
    let usr = users.find(u => u.email === currentUser.email);

    let title = 'Follow up on ' + props.title;
    if (props.project) title += ' at ' + props.project;

    // We are going to try find the index of metadata "title" and "assigned to"
    // to fill them in the submission later
    for (let i = 0; i < version.pages.length; i++) {
      let page = version.pages[i];
      if (page.controls)
        for (let j = 0; j < page.controls.length; j++) {
          if (page.controls[j].config) {
            let config = JSON.parse(page.controls[j].config!);
            if (config['hasMetaData'] && config['metaDataKey']) {
              if (config['metaDataKey'] === METADATA_KEYS.TITLE)
                answers.push({
                  controlId: page.controls[j].controlId,
                  controlTypeId: page.controls[j].controlId,
                  answer: title,
                } as Submission_answer);
              else if (config['metaDataKey'] === 'AssignedTo')
                answers.push({
                  controlId: page.controls[j].controlId,
                  controlTypeId: page.controls[j].controlId,
                  answer: JSON.stringify({
                    mongoId: usr?._id,
                    SQLServerId: usr?.SQLServerId,
                  }),
                } as Submission_answer);
            }
          }
        }
    }

    return answers;
  }

  return (
    <ScrollView
      style={{
        flex: 1,
        backgroundColor: Colors.white,
        padding: 20,
      }}>
      <View style={DynamicPageStyleSheet.confirmationContainer}>
        <Animated.View
          style={{
            width: circleSize,
            height: circleSize,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: Colors.darkGreen,
            borderRadius: circleSize,
          }}>
          <Icon icon={'input-check'} color={Colors.white} size={48} />
        </Animated.View>
        <Animated.View
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            opacity: backgroundOpacity,
          }}>
          <Image
            resizeMode='cover'
            style={{width: '100%', height: '100%'}}
            source={Images.CONFIRMATION}
          />
        </Animated.View>
        <Animated.View
          style={{
            position: 'absolute',
            left: 24,
            bottom: textBottom,
          }}>
          <Text style={DynamicPageStyleSheet.confirmationText}>
            Submitted.{'\n'}Thank you.
          </Text>
        </Animated.View>
      </View>
      <Pressable
        style={({pressed}) => [
          CommonStyleSheet.greenButton,
          {width: '100%', marginTop: 12},
          pressed && {
            opacity: 0.8,
          },
        ]}
        onPress={() => goToDashboard()}>
        <Text style={CommonStyleSheet.greenButtonText}>Home</Text>
      </Pressable>
      <Pressable
        style={({pressed}) => [
          CommonStyleSheet.whiteButton,
          {width: '100%', marginTop: 12},
          pressed && {
            backgroundColor: Colors.darkGreenTransparent,
          },
        ]}
        onPress={closeForm}>
        <Text style={CommonStyleSheet.whiteButtonText}>Return to Reports</Text>
      </Pressable>
      <Pressable
        style={({pressed}) => [
          CommonStyleSheet.whiteButton,
          {
            flexDirection: 'row',
            justifyContent: 'center',
            gap: 3,
            width: '100%',
            paddingHorizontal: 3,
            marginTop: 12,
          },
          pressed && {
            backgroundColor: Colors.darkGreenTransparent,
          },
        ]}
        onPress={() => followUp()}>
        {/* TODO: Add event when follow up is implemented */}
        <Icon icon={'task'} color={Colors.darkGreen} size={48} />
        <Text style={CommonStyleSheet.whiteButtonText}>
          Remind Me To Follow Up On This Report
        </Text>
      </Pressable>
    </ScrollView>
  );
};

export default FormConfirmation;
